import React, { useEffect, useState } from "react";
import api, { formatINR } from "../../lib/api";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, ShoppingCart, Users, Award, MapPin, Filter } from "lucide-react";

const CATEGORY_COLORS = ["#0369a1", "#059669", "#7c3aed", "#d97706", "#dc2626", "#0891b2", "#65a30d", "#c026d3", "#ea580c", "#0284c7"];

function KPI({ icon: Icon, label, value, sub, testId }) {
    return (
        <div className="bg-white border border-stone-200 p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
                <Icon className="w-4 h-4 text-sky-700" />
            </div>
            <div className="font-serif text-3xl text-stone-900">{value}</div>
            {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function AdminSalesAnalytics() {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [byState, setByState] = useState([]);
    const [daily, setDaily] = useState([]);
    const [byCategory, setByCategory] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [funnel, setFunnel] = useState([]);
    const [byOrderType, setByOrderType] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async (d) => {
        setBusy(true);
        try {
            const [s, st, dl, cat, tp, f, ot] = await Promise.all([
                api.get("/admin/sales-analytics/summary", { params: { days: d } }),
                api.get("/admin/sales-analytics/by-state", { params: { days: d } }),
                api.get("/admin/sales-analytics/daily", { params: { days: d } }),
                api.get("/admin/sales-analytics/by-category", { params: { days: d } }),
                api.get("/admin/sales-analytics/top-products", { params: { days: d } }),
                api.get("/admin/sales-analytics/funnel", { params: { days: d } }),
                api.get("/admin/sales-analytics/by-order-type", { params: { days: d } }),
            ]);
            setSummary(s.data);
            setByState(st.data.rows || []);
            setDaily(dl.data.rows || []);
            setByCategory(cat.data.rows || []);
            setTopProducts(tp.data.rows || []);
            setFunnel(f.data.steps || []);
            setByOrderType(ot.data.rows || []);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

    const maxFunnel = Math.max(1, ...(funnel.map((s) => s.count) || [1]));

    return (
        <div className="space-y-6" data-testid="admin-sales-analytics-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-sky-700">Business</div>
                    <h1 className="font-serif text-4xl text-stone-900">Sales Analytics</h1>
                    <p className="text-sm text-stone-500 mt-1">Live aggregations from the order collection — no external analytics service required.</p>
                </div>
                <div className="flex gap-2">
                    {[7, 30, 90, 365].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`text-xs px-3 py-1.5 border ${days === d ? "bg-sky-700 text-stone-50 border-sky-700" : "bg-white border-stone-300 hover:bg-stone-50"}`}
                            data-testid={`sales-range-${d}`}
                        >
                            {d === 365 ? "1 yr" : `${d} days`}
                        </button>
                    ))}
                </div>
            </div>

            {/* KPI row */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KPI icon={ShoppingCart} label="Orders" value={summary.total_orders} sub={`${summary.delivered} shipped/delivered · ${summary.cancelled} cancelled`} testId="sa-kpi-orders" />
                    <KPI icon={TrendingUp} label="Net revenue" value={formatINR(summary.net_revenue || 0)} sub={`AOV ${formatINR(summary.avg_order_value || 0)}`} testId="sa-kpi-revenue" />
                    <KPI icon={Users} label="Unique buyers" value={summary.unique_buyers} testId="sa-kpi-buyers" />
                    <KPI icon={Award} label="Best category" value={byCategory[0]?.category || "—"} sub={byCategory[0] ? `${formatINR(byCategory[0].revenue)}` : ""} testId="sa-kpi-best-cat" />
                </div>
            )}

            {/* Daily revenue chart */}
            <div className="bg-white border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Daily revenue & orders</div>
                <div style={{ width: "100%", height: 280 }}>
                    <ResponsiveContainer>
                        <LineChart data={daily}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
                            <YAxis yAxisId="rev" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                            <YAxis yAxisId="ord" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip formatter={(v, n) => n === "revenue" ? formatINR(v) : v} />
                            <Line yAxisId="rev" type="monotone" dataKey="revenue" stroke="#0369a1" strokeWidth={2} dot={false} name="Revenue" />
                            <Line yAxisId="ord" type="monotone" dataKey="orders" stroke="#059669" strokeWidth={2} dot={false} name="Orders" />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {/* By state */}
                <div className="bg-white border border-stone-200 p-5">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2"><MapPin className="w-4 h-4" />Revenue by state</div>
                    {byState.length ? (
                        <div style={{ width: "100%", height: Math.max(240, Math.min(500, byState.length * 30)) }}>
                            <ResponsiveContainer>
                                <BarChart data={byState.slice(0, 15)} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                    <YAxis dataKey="state" type="category" tick={{ fontSize: 11 }} width={100} />
                                    <Tooltip formatter={(v) => formatINR(v)} />
                                    <Bar dataKey="revenue" fill="#0369a1" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <div className="text-sm text-stone-500 py-16 text-center">No orders yet in this range.</div>}
                </div>

                {/* By category pie */}
                <div className="bg-white border border-stone-200 p-5">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Revenue by category</div>
                    {byCategory.length ? (
                        <div style={{ width: "100%", height: 300 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={byCategory} dataKey="revenue" nameKey="category" outerRadius={100} label={(e) => e.category}>
                                        {byCategory.map((_, i) => <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => formatINR(v)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <div className="text-sm text-stone-500 py-16 text-center">No category data yet.</div>}
                </div>
            </div>

            {/* Funnel */}
            <div className="bg-white border border-stone-200 p-5">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2"><Filter className="w-4 h-4" />Conversion funnel</div>
                {funnel.length === 0 ? (
                    <div className="text-sm text-stone-500">No data yet.</div>
                ) : (
                    <ol className="space-y-2" data-testid="sa-funnel">
                        {funnel.map((s, i) => {
                            const width = Math.max(4, (s.count / maxFunnel) * 100);
                            return (
                                <li key={s.key} className="grid grid-cols-12 gap-3 items-center" data-testid={`funnel-step-${s.key}`}>
                                    <span className="col-span-4 text-sm text-stone-800">{i + 1}. {s.label}</span>
                                    <div className="col-span-6 relative h-8 bg-stone-100">
                                        <div className="h-8 bg-sky-700 flex items-center px-2 text-xs text-white font-mono" style={{ width: `${width}%` }}>{s.count}</div>
                                    </div>
                                    <span className="col-span-2 text-right text-xs text-emerald-700 font-mono">{s.conversion_pct}%</span>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            {/* Top products */}
            <div className="bg-white border border-stone-200">
                <div className="p-4 text-xs uppercase tracking-widest text-stone-500 border-b border-stone-200">Top-selling products</div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Product</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Orders</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Units</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Revenue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {topProducts.length === 0 ? (
                            <tr><td colSpan={4} className="p-8 text-center text-stone-400">No orders yet.</td></tr>
                        ) : topProducts.map((p) => (
                            <tr key={p.product_id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`top-prod-${p.product_id}`}>
                                <td className="p-3 text-stone-800">{p.product_name}</td>
                                <td className="p-3 text-right font-mono">{p.orders}</td>
                                <td className="p-3 text-right font-mono">{p.quantity}</td>
                                <td className="p-3 text-right font-mono text-sky-700">{formatINR(p.revenue)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* By order type */}
            {byOrderType.length > 0 && (
                <div className="bg-white border border-stone-200 p-5">
                    <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Revenue by channel</div>
                    <div className="grid md:grid-cols-3 gap-3">
                        {byOrderType.map((r) => (
                            <div key={r.order_type} className="border border-stone-200 p-4" data-testid={`sa-channel-${r.order_type}`}>
                                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">{r.order_type}</div>
                                <div className="font-serif text-2xl text-stone-900">{formatINR(r.revenue)}</div>
                                <div className="text-xs text-stone-500 mt-1">{r.orders} orders</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {busy && <div className="text-xs text-stone-500">Refreshing…</div>}
        </div>
    );
}
